/** Requests to parties outside the file: market-study data asked of undertakings, technical opinions asked of regulators (G2G). */
export default {
  title: "Data and opinion requests",
  subtitle: "What the Department has asked of undertakings for its market studies and of regulators for their technical opinions, and where each answer stands.",
  kind: { MarketData: "Market-study data", TechnicalOpinion: "Technical opinion" },
  kindLong: { MarketData: "Market-study data request ({{marketDataBasis}})", TechnicalOpinion: "Technical opinion request ({{opinionBasis}})" },
  status: {
    Sent: "Sent",
    Opened: "Opened",
    Submitted: "Answer received",
    ClarificationRequested: "Clarification asked",
    Completed: "Closed",
    Withdrawn: "Withdrawn",
    Lapsed: "Lapsed",
  },
  completed: { MarketData: "Accepted", TechnicalOpinion: "Considered" },
  filters: { all: "All", open: "Open", kind: "Kind" , withClosed: "Including closed" },
  columns: { reference: "Reference", addressee: "Addressee", subject: "Subject", due: "Answer by", status: "Status", about: "About" },
  overdue: "Overdue",
  empty: "No requests yet",
  emptyHint: "Data requests are sent from a market study in the Data Bank; technical opinions from a case.",
  study: {
    title: "Data requests to undertakings",
    hint: "Each undertaking answers on the portal with its figures and supporting documents ({{marketDataBasis}}). Accepted sales lines are filed in the Data Bank as the undertaking's self-report.",
    new: "Request data",
    empty: "No data requested for this study yet.",
    file: "Market-study file:",
    testFile: "Test file: its requests reach no one outside the Department",
    noFile: "Not tied to a market-study file: its requests go to the undertakings by portal notice and e-mail. Tie it to its file before the first request, so that on a test file they reach no one.",
    noFileSent: "Not tied to a market-study file; requests have already gone out.",
    tie: "Tie to a market-study file",
    tieConfirm: "Tie the study",
    fileTied: "Study tied to its market-study file.",
  },
  opinions: {
    tab: "Technical opinions",
    title: "Technical opinions",
    lead: "Opinions of sector regulators and emirate authorities on this file ({{opinionBasis}}).",
    hint: "Ask a sector regulator or an emirate authority for its opinion ({{opinionBasis}} – {{opinionPeriod}} to answer). On a concentration the request interrupts the review period until the opinion arrives (Article 14(1)(b)).",
    new: "Ask for an opinion",
    askThis: "Ask for its opinion",
    empty: "No technical opinion has been asked on this file.",
    linked: "{{reference}} · {{status}}",
  },
  newData: {
    title: "Request data for the market study",
    description: "One request per undertaking, each with its own reference. The undertaking is told on the portal and by e-mail.",
    recipients: "Undertakings",
    fromMarket: "In this market",
    noMarketEntities: "The study's market has no undertakings with shares yet. Search the Data Bank or add one by name.",
    search: "Search the Data Bank",
    searchPlaceholder: "Name, licence or LEI",
    addOther: "Add an undertaking not in the Data Bank",
    otherName: "Undertaking name",
    contactName: "Contact person",
    contactEmail: "Contact e-mail",
    contactEmailHint: "Required when the undertaking has no portal account yet.",
    portalAccount: "On the portal",
    noPortalAccount: "No portal account",
    remove: "Remove",
    subject: "Subject",
    purpose: "What the data is for",
    purposeHint: "Shown to the undertaking: the study and why its figures are needed.",
    items: "What is asked",
    itemKind: { Text: "Written answer", Number: "Figure", Sales: "Sales by product line", Document: "Documents" },
    required: "Required",
    addQuestion: "Add a question",
    years: "Years",
    dueOn: "Answer by",
    dueHint: "{{days}} working days unless you choose another date.",
    send: "Send {{count}} requests", send_one: "Send {{count}} request",
    sent: "{{count}} requests sent", sent_one: "{{count}} request sent",
  },
  newOpinion: {
    title: "Ask for a technical opinion",
    description: "{{contentBasis}}: the request is written, summarises the file, states the practices and the questions, and is confidential.",
    body: "Regulator or authority",
    suggested: "Suggested for the markets of this file",
    regulators: "Sector regulators",
    authorities: "Emirate authorities",
    coordinationEntry: "Coordination entry",
    newEntry: "Add a new entry",
    focalName: "Focal point",
    focalEmail: "Focal point e-mail",
    focalEmailHint: "The request is e-mailed here; whoever signs in with this address answers it. It is remembered for the next request.",
    subject: "Subject",
    summary: "Summary of the file",
    context: "The practices, transaction or application in question",
    questions: "Questions",
    addQuestion: "Add a question",
    dueOn: "Answer due {{date}} ({{opinionPeriod}}, {{opinionBasis}})",
    send: "Send the request",
    sent: "Request sent",
    interruptsClock: "Sending it interrupts the review period until the opinion arrives.",
  },
  review: {
    request: "The request",
    answer: "The answer",
    noAnswer: "No answer yet.",
    earlierRounds: "Earlier answers, as submitted",
    round: "Round {{n}} · submitted {{date}}",
    files: "Files",
    ministryFile: "Shared by the Department",
    recipientFile: "From the addressee",
    attach: "Attach a document for the addressee",
    attached: "Document attached",
    history: "History",
    respondent: "Answered by {{name}}",
    confidential: "Confidential",
    confidentialityNote: "Confidentiality",
    salesFiled: "{{count}} sales lines filed in the Data Bank", salesFiled_one: "{{count}} sales line filed in the Data Bank",
    testFile: "Test file: reaches no one outside the Department",
    crossCheckTitle: "{{count}} figures the Data Bank's other sources do not bear out", crossCheckTitle_one: "{{count}} figure the Data Bank's other sources do not bear out",
    crossCheckHint: "Check each against the source named before accepting: the answer is filed as the undertaking's own report. Ask for a clarification, or accept it with your reasons.",
    crossCheckReasons: "Reasons for accepting the figures the Data Bank does not bear out",
    positions: {
      NoConcerns: "No regulatory concerns",
      ConcernsAddressable: "Concerns that conditions can address",
      SeriousConcerns: "Serious concerns",
      OutsideRemit: "Outside the regulator's remit",
    },
    position: "Position",
    statement: { MarketData: "Notes on the figures", TechnicalOpinion: "The opinion" },
    marketAssessment: "State of competition",
    conditions: "Proposed conditions",
    sources: "Sources",
    contact: "Addressed to",
    issuedBy: "Sent by {{name}}",
    accept: { MarketData: "Accept and file in the Data Bank", TechnicalOpinion: "Mark as considered" },
    acceptNote: "Note for the file (optional)",
    accepted: { MarketData: "Accepted – {{count}} sales lines filed", MarketData_one: "Accepted – {{count}} sales line filed", TechnicalOpinion: "Opinion recorded as considered" },
    clarify: "Ask for a clarification",
    clarifyNote: "What needs clarifying",
    clarifyDue: "New date",
    clarified: "Clarification asked",
    withdraw: "Withdraw",
    withdrawReason: "Reason",
    withdrawn: "Request withdrawn",
    lapse: "Close without an answer",
    lapseNote: "Note",
    lapsed: "Request closed",
    extension: "{{name}} asks for more time until {{date}}",
    grant: "Grant",
    refuse: "Refuse",
    granted: "More time granted",
    refused: "Extension refused",
    send: "Send",
  },
  events: {
    Sent: "Sent",
    Opened: "Opened by the addressee",
    Submitted: "Answer submitted",
    ClarificationRequested: "Clarification asked",
    ExtensionRequested: "More time asked",
    ExtensionGranted: "More time granted",
    ExtensionRefused: "Extension refused",
    Reminder: "Reminder sent",
    Completed: "Closed",
    Withdrawn: "Withdrawn",
    Lapsed: "Closed without answer",
  },
  itemKinds: { Text: "Written answer", Number: "Figure", Sales: "Sales table", Document: "Documents" },
  sales: { productLine: "Product line", hsCode: "HS", year: "Year", flow: "Flow", value: "Value (AED)", volume: "Volume", flows: { Domestic: "UAE", Import: "Import", Export: "Export" } },
} as const;
